import React from 'react';
import { FormattedMessage } from 'react-intl';
import QRCode from 'react-qr-code';

import { Button } from 'ui-components/components/button';
import { CopyToClipboardButton } from 'ui-components/components/copy-to-clipboard-button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Text, TextType } from 'ui-components/components/text';

import { PHONE_VERIFICATION_STATES } from '../../../../../../../authentication/constants';
import { MANAGEMENT_TYPES } from '../../../../../../../global/step-up-authentication/constants';
import { useOnComponentMount } from '../../../../../../../hooks/use-on-component-mount';
import { messages } from '../../messages';
import style from '../add-mfa-enrollment.module.less';
import { EnrollmentErrorBanner } from '../enrollment-error-banner';

/**
 * @typedef {import('../../../../../../../global/step-up-authentication/constants').Auth0ManagementMfaType} Auth0ManagementMfaType
 * @typedef {keyof typeof PHONE_VERIFICATION_STATES} PhoneVerificationState
 */

/**
 * @param {object} props
 * @param {object} props.advanceToChallengePhase
 * @param {() => void} props.onCancel
 * @param {() => void} props.onStartVerification
 * @param {string} props.qrCodeUri
 * @param {string} props.secret
 * @param {{ message: string }} [props.error]
 * @param {() => void} props.resetError
 * @param {boolean} [props.isUnskippable]
 * @param {PhoneVerificationState} [props.phoneVerificationState]
 * @param {Auth0ManagementMfaType} [props.currentMfaType]
 * @param {(arg0: Auth0ManagementMfaType) => void} [props.switchMfaType]
 * @returns {React.JSX.Element}
 */
export const AuthenticatorEnrollmentForm = ({
  advanceToChallengePhase,
  currentMfaType,
  onCancel,
  error,
  resetError,
  onStartVerification,
  phoneVerificationState,
  qrCodeUri,
  secret,
  switchMfaType,
  isUnskippable = false,
}) => {
  // fetch the TOTP secret to display
  useOnComponentMount(() => {
    onStartVerification();
  });

  const shouldShowForcedMfaContext =
    phoneVerificationState === PHONE_VERIFICATION_STATES.BANNER ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.MODAL ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.UNSKIPPABLE;

  return (
    <>
      <ModalBody className={style['add-enrollment-modal-content']}>
        <FlexColumn gap={Gap.Space16}>
          <Text
            textType={TextType.H3}
            value={shouldShowForcedMfaContext ? messages.authenticatorEnrollmentFormTitleForced : messages.authenticatorEnrollmentFormTitle}
          />
          <Text
            className={style['authenticator-instructions']}
            textType={TextType.Paragraph}
            value={shouldShowForcedMfaContext ? messages.authenticatorEnrollmentFormTextForced : messages.authenticatorEnrollmentFormText}
          />
          {shouldShowForcedMfaContext ? (
            <Text
              className={style['authenticator-setup-instructions']}
              textType={TextType.Paragraph}
              value={messages.authenticatorSetupInstructions}
            />
          ) : null}
          <FlexColumn className={style['authenticator-secret']}>
            {secret ? (
              <>
                <QRCode value={qrCodeUri} />
                <Text element='p' textType={TextType.Label} value={messages.authenticatorEnrollmentFormSecretLabel} />
                <FlexRow className={style['authenticator-code']} gap={Gap.Space8}>
                  <Text textType={TextType.H4} value={secret} />
                  <CopyToClipboardButton textToCopy={secret} tooltipMessageDescriptor={messages.authenticatorCopyButtonTooltip} />
                </FlexRow>
              </>
            ) : null}
          </FlexColumn>
          {shouldShowForcedMfaContext ? (
            <Text
              className={style['authenticator-security-explanation']}
              textType={TextType.Paragraph}
              value={messages.authenticatorSecurityExplanation}
            />
          ) : null}
          <EnrollmentErrorBanner error={error} resetError={resetError} />
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow className={style.footer} gap={Gap.Space16}>
          {switchMfaType && isUnskippable ? (
            <Button
              onClick={() => switchMfaType(currentMfaType === MANAGEMENT_TYPES.PHONE ? MANAGEMENT_TYPES.TOTP : MANAGEMENT_TYPES.PHONE)}
              theme='secondary'
            >
              <FormattedMessage {...messages.authenticateAnotherWayButton} />
            </Button>
          ) : null}
          {!isUnskippable && (
            <Button onClick={onCancel} theme='tertiary'>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
          )}
          <Button onClick={advanceToChallengePhase} theme='primary'>
            <FormattedMessage {...messages.buttonVerifyCode} />
          </Button>
        </FlexRow>
      </ModalFooter>
    </>
  );
};
